import { useState, useEffect, useMemo } from "react";
import { PageHeader, SectionCard } from "@/components/Primitives";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { Copy, Save, Sparkles, AlertTriangle, Plus, Trash2, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Approved merge fields only.
const FIELDS = [
  "business_name", "contact_name", "salesperson_name", "product_name",
  "product_description", "product_price", "contract_term", "zone", "renewal_date", "booking_link",
];

export default function Composer() {
  const { user: me } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [renewals, setRenewals] = useState({});

  const [bizId, setBizId] = useState("");
  const [contactId, setContactId] = useState("");
  const [productName, setProductName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("Hi {{contact_name}},\n\nThank you for your interest in advertising with ChitChat. Here are the details for {{product_name}}:\n\n{{product_description}}\n\nCurrent price: {{product_price}} ({{contract_term}}) for the {{zone}} zone.\n\nBest regards,\n{{salesperson_name}}");
  const [tplName, setTplName] = useState("");
  const [editingTpl, setEditingTpl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    api.get("/businesses").then((r) => setBusinesses(r.data.items));
    api.get("/contacts").then((r) => setContacts(r.data.items));
    api.get("/products").then((r) => setProducts(r.data.items.filter((p) => p.active !== false)));
    api.get("/templates").then((r) => setTemplates(r.data.items));
    api.get("/renewals").then((r) => { const m = {}; (r.data.items || []).forEach((x) => { m[x.business_id] = x; }); setRenewals(m); }).catch(() => {});
  }, []);

  const biz = businesses.find((b) => b.id === bizId);
  const contact = contacts.find((c) => c.id === contactId);
  const product = products.find((p) => p.name === productName);
  const bizContacts = contacts.filter((c) => c.business_id === bizId);

  const values = useMemo(() => ({
    business_name: biz?.name,
    contact_name: contact ? `${contact.first_name} ${contact.last_name || ""}`.trim() : null,
    salesperson_name: me?.name,
    product_name: product?.name,
    product_description: product?.customer_facing_description || product?.description || product?.detailed_description,
    product_price: product ? fmtMoney(product.base_price ?? product.founding_price ?? product.regular_price) : null,
    contract_term: product?.contract_term || (product?.billing_type === "Monthly" ? "Monthly" : null),
    zone: biz?.zone,
    renewal_date: renewals[bizId]?.renewal_date,
    booking_link: me?.booking_url || null,
  }), [biz, contact, product, me, renewals, bizId]);

  const BOOKING_LABEL = "Schedule a Call";
  const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const resolveText = (text) => text.replace(/{{\s*(\w+)\s*}}/g, (_, k) => {
    if (k === "booking_link") return me?.booking_url ? `${BOOKING_LABEL}: ${me.booking_url}` : "[MISSING: booking_link]";
    return values[k] != null && values[k] !== "" ? values[k] : `[MISSING: ${k}]`;
  });
  const resolveHtml = (text) => esc(text).replace(/{{\s*(\w+)\s*}}/g, (_, k) => {
    if (k === "booking_link") {
      return me?.booking_url
        ? `<a href="${esc(me.booking_url)}" style="color:#0094A8;font-weight:600;">${BOOKING_LABEL}</a>`
        : `<span style="color:#C42607;">[Set your Scheduling Link]</span>`;
    }
    const v = values[k];
    return v != null && v !== "" ? esc(v) : `[MISSING: ${k}]`;
  }).replace(/\n/g, "<br>");
  const resolvedSubject = resolveText(subject);
  const usesBooking = /{{\s*booking_link\s*}}/.test(body) || /{{\s*booking_link\s*}}/.test(subject);
  const noBookingUrl = usesBooking && !me?.booking_url;
  const missing = useMemo(() => {
    const used = [...body.matchAll(/{{\s*(\w+)\s*}}/g), ...subject.matchAll(/{{\s*(\w+)\s*}}/g)].map((m) => m[1]);
    return [...new Set(used)].filter((k) => values[k] == null || values[k] === "");
  }, [body, subject, values]);

  const insert = (token) => setBody((b) => b + (b.endsWith("\n") || !b ? "" : " ") + `{{${token}}}`);
  const insertProductBlock = () => {
    if (!product) { toast.error("Select a product first"); return; }
    setBody((b) => b + `\n\n${product.name}\n${product.customer_facing_description || product.description || ""}\nPrice: {{product_price}}`);
  };

  const copy = async () => {
    if (noBookingUrl) { toast.error("Set your Scheduling Link first — profile menu (top-right) → Scheduling Link."); return; }
    if (missing.length && !window.confirm(`Warning: ${missing.length} merge field(s) have no data and will appear as [MISSING]. Copy anyway?`)) return;
    const html = (resolvedSubject ? `<p><b>${esc(resolvedSubject)}</b></p>` : "") + `<div>${resolveHtml(body)}</div>`;
    const text = (resolvedSubject ? `Subject: ${resolvedSubject}\n\n` : "") + resolveText(body);
    try {
      if (window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        })]);
        toast.success("Copied — paste into Outlook; the Schedule a Call link stays clickable");
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
      }
    } catch { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
  };

  const saveTemplate = async () => {
    if (!tplName) { toast.error("Name your template"); return; }
    try {
      if (editingTpl) { await api.put(`/templates/${editingTpl}`, { name: tplName, subject, body }); toast.success("Template updated"); }
      else { await api.post("/templates", { name: tplName, subject, body }); toast.success("Template saved"); }
      const r = await api.get("/templates"); setTemplates(r.data.items); setEditingTpl(null);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const loadTemplate = (t) => { setSubject(t.subject || ""); setBody(t.body || ""); setTplName(t.name); setEditingTpl(t.id); };
  const delTemplate = async (t) => { try { await api.delete(`/templates/${t.id}`); setTemplates((ts) => ts.filter((x) => x.id !== t.id)); if (editingTpl === t.id) setEditingTpl(null); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };

  return (
    <div data-testid="page-composer">
      <PageHeader title="Message Composer" subtitle="Build reusable messages with live product info and approved merge fields. Preview, copy or save as a template." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <SectionCard title="Recipient & Product">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Business</Label>
                <Select value={bizId} onValueChange={(v) => { setBizId(v); setContactId(""); }}>
                  <SelectTrigger className="mt-1" data-testid="comp-business"><SelectValue placeholder="Select business…" /></SelectTrigger>
                  <SelectContent>{businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Contact</Label>
                <Select value={contactId} onValueChange={setContactId}>
                  <SelectTrigger className="mt-1" data-testid="comp-contact"><SelectValue placeholder="Select contact…" /></SelectTrigger>
                  <SelectContent>{(bizId ? bizContacts : contacts).map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="sm:col-span-2"><Label className="text-xs">Product / Service</Label>
                <Select value={productName} onValueChange={setProductName}>
                  <SelectTrigger className="mt-1" data-testid="comp-product"><SelectValue placeholder="Select product…" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name} — {fmtMoney(p.base_price ?? p.founding_price ?? p.regular_price)}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          </SectionCard>

          <SectionCard title="Message" action={<Button size="sm" variant="outline" onClick={insertProductBlock} data-testid="comp-insert-product"><Sparkles className="h-4 w-4" />Insert Product</Button>}>
            <Label className="text-xs">Subject</Label>
            <Input className="mt-1 mb-3" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="comp-subject" />
            <Label className="text-xs">Body</Label>
            <Textarea className="mt-1 min-h-[220px] font-mono text-sm" value={body} onChange={(e) => setBody(e.target.value)} data-testid="comp-body" />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {FIELDS.map((f) => <button key={f} onClick={() => insert(f)} className="rounded-full bg-slate-100 hover:bg-[#00C9E0]/15 hover:text-[#0094A8] px-2.5 py-1 text-[11px] font-medium text-slate-600 transition" data-testid={`comp-field-${f}`}>{`{{${f}}}`}</button>)}
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard title="Preview" action={<Button size="sm" variant="outline" onClick={() => setShowPreview((v) => !v)} data-testid="comp-toggle-preview"><Eye className="h-4 w-4" />{showPreview ? "Hide" : "Show"}</Button>}>
            {missing.length > 0 && (
              <div className="mb-3 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-2.5 text-xs text-[#B45309]" data-testid="comp-missing">
                <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Missing data for: {missing.join(", ")}</p>
              </div>
            )}
            {noBookingUrl && (
              <div className="mb-3 rounded-lg border border-[#C42607]/30 bg-[#C42607]/5 p-2.5 text-xs text-[#C42607]" data-testid="comp-no-booking">
                <p className="font-semibold">Your Scheduling Link isn't set.</p>
                <p className="mt-0.5">Add it from the top-right profile menu → “Scheduling Link”, or remove the “Schedule a Call” line before sending.</p>
              </div>
            )}
            {showPreview && (
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm" data-testid="comp-preview">
                {resolvedSubject && <p className="font-semibold mb-2">{resolvedSubject}</p>}
                <div dangerouslySetInnerHTML={{ __html: resolveHtml(body) }} />
              </div>
            )}
            <Button className="w-full mt-3" onClick={copy} data-testid="comp-copy"><Copy className="h-4 w-4" />Copy to Clipboard</Button>
          </SectionCard>

          <SectionCard title="Templates">
            <div className="flex gap-2">
              <Input placeholder="Template name" value={tplName} onChange={(e) => setTplName(e.target.value)} data-testid="comp-tpl-name" />
              <Button size="sm" onClick={saveTemplate} data-testid="comp-tpl-save"><Save className="h-4 w-4" />{editingTpl ? "Update" : "Save"}</Button>
            </div>
            <div className="mt-3 divide-y divide-slate-100">
              {templates.length === 0 ? <p className="text-xs text-slate-400 py-2">No saved templates yet.</p> : templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2" data-testid={`comp-tpl-${t.id}`}>
                  <button onClick={() => loadTemplate(t)} className="text-sm text-slate-700 hover:text-[#0094A8] truncate text-left">{t.name}</button>
                  {me?.role !== "sales_rep" && me?.role !== "readonly" && <button onClick={() => delTemplate(t)} className="text-slate-300 hover:text-[#C42607]"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
